import { db } from "../firebase/config";
import {
  collection,
  getDocs,
  query,
  orderBy,
  limit,
  doc,
  getDoc,
  setDoc,
} from "firebase/firestore";
import {
  consultationsCollection,
  withdrawalsCollection,
  settingsCollection,
} from "../firebase/firestore";

/**
 * Money model
 *   - A booking credits the doctor:  doctor fee − commission %  (coupons and free
 *     bookings do not reduce it). Legacy bookings: (amount paid − platform fee) − commission %.
 *   - The credit is "held" until the consultation is completed, then becomes
 *     withdrawable. Cancelled bookings are not credited.
 *   - Withdrawals (collection `withdrawals`) subtract from the withdrawable
 *     balance: paid/approved ones are gone, pending ones are held.
 *
 * Platform fee + commission live in Settings/finance and are snapshotted on
 * each booking (platformFee, commissionPercent) by the web booking server;
 * older bookings fall back to the current settings.
 */

export const DEFAULT_FINANCE = { platformFee: 50, doctorCommissionPercent: 0 };

const num = (v, fallback = 0) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : fallback;
};
const round2 = (n) => Math.round(n * 100) / 100;

export async function getFinanceSettings() {
  try {
    const snap = await getDoc(doc(db, settingsCollection, "finance"));
    const d = snap.exists() ? snap.data() : {};
    return {
      platformFee: num(d.platformFee, DEFAULT_FINANCE.platformFee),
      doctorCommissionPercent: num(
        d.doctorCommissionPercent,
        DEFAULT_FINANCE.doctorCommissionPercent
      ),
    };
  } catch (e) {
    return { ...DEFAULT_FINANCE };
  }
}

export async function saveFinanceSettings({ platformFee, doctorCommissionPercent }) {
  const fee = Math.round(num(platformFee, NaN));
  const pct = num(doctorCommissionPercent, NaN);
  if (!Number.isFinite(fee) || fee < 0) throw new Error("Platform fee must be 0 or more");
  if (!Number.isFinite(pct) || pct < 0 || pct > 100)
    throw new Error("Commission must be between 0 and 100");
  await setDoc(
    doc(db, settingsCollection, "finance"),
    { platformFee: fee, doctorCommissionPercent: pct, updatedAt: Date.now() },
    { merge: true }
  );
  return { platformFee: fee, doctorCommissionPercent: pct };
}

/** Turn one paid Consultations document into a ledger credit. */
export function creditFromConsultation(c, settings) {
  const paid = num(c.payment?.amountPaise) / 100;
  const platformFee = num(c.platformFee, settings.platformFee);
  const commissionPercent = num(c.commissionPercent, settings.doctorCommissionPercent);
  // Bookings carry a doctorFee snapshot: the doctor is paid that in full, even
  // for free (100% coupon) bookings. Older bookings fall back to paid − fee.
  const base = c.doctorFee !== undefined && Number.isFinite(Number(c.doctorFee))
    ? Math.max(num(c.doctorFee), 0)
    : Math.max(paid - platformFee, 0);
  const commission = round2((base * commissionPercent) / 100);
  const doctorEarning = round2(base - commission);

  let status = "held"; // paid, consultation not completed yet
  if (c.cancelledByDoctor) status = "cancelled";
  else if (c.videoConsultDone === true || num(c.actualEndTime) > 0) status = "completed";

  const details = c.extras?.patientDetails || {};
  return {
    id: c.id,
    type: "credit",
    date: num(c.bookedAt) || num(c.createdAt) || num(c.consultationTime),
    consultationTime: num(c.consultationTime),
    doctorId: c.doctorId || c.participants?.[1] || "",
    doctorName: c.doctorName || "Doctor",
    patientUid: c.participants?.[0] || "",
    patientName: details.patientName || c.patientName || "Patient",
    patientAge: details.patientAge || "",
    patientGender: details.gender || "",
    patientRelation: details.relationship || "",
    patientPhone: c.patientWhatsApp || "",
    amountPaid: paid,
    platformFee,
    commissionPercent,
    commission,
    amount: doctorEarning, // what the doctor is credited
    status,
    couponCode: c.appliedCoupon || "",
    couponDiscount: num(c.couponDiscount),
    paymentId: c.payment?.paymentId || "",
    orderId: c.payment?.orderId || "",
    paymentVia: c.payment?.via || "",
  };
}

const WITHDRAWN_STATES = ["paid", "approved", "completed", "processed"];

export function entryFromWithdrawal(w) {
  const raw = String(w.status || "pending").toLowerCase();
  const status = WITHDRAWN_STATES.includes(raw)
    ? "paid"
    : raw === "rejected" || raw === "failed" || raw === "cancelled"
    ? "rejected"
    : "pending";
  return {
    id: w.id,
    type: "withdrawal",
    date: num(w.requestedAt) || num(w.createdAt) || num(w.date),
    processedAt: num(w.processedAt) || num(w.paidAt),
    doctorId: w.doctorId || w.userId || "",
    doctorName: w.doctorName || w.userName || "Doctor",
    amount: num(w.amount),
    status,
    method: w.method || w.payoutMethod || "",
    destination: w.upiId || w.upi || w.bankAccount || w.bankName || "",
    note: w.rejectionReason || w.note || "",
  };
}

/** Per-doctor totals that answer "how much can they withdraw?". */
export function buildBalances(entries) {
  const map = new Map();
  const row = (e) => {
    if (!map.has(e.doctorId || e.doctorName)) {
      map.set(e.doctorId || e.doctorName, {
        doctorId: e.doctorId,
        doctorName: e.doctorName,
        credited: 0, // all non-cancelled credits
        completed: 0, // credits whose consultation is done (withdrawable base)
        held: 0, // paid, consultation not completed
        withdrawn: 0,
        pendingWithdrawal: 0,
        available: 0,
      });
    }
    return map.get(e.doctorId || e.doctorName);
  };
  for (const e of entries) {
    const r = row(e);
    if (e.type === "credit") {
      if (e.status === "cancelled") continue;
      r.credited += e.amount;
      if (e.status === "completed") r.completed += e.amount;
      else r.held += e.amount;
    } else if (e.status === "paid") r.withdrawn += e.amount;
    else if (e.status === "pending") r.pendingWithdrawal += e.amount;
  }
  const rows = [...map.values()].map((r) => ({
    ...r,
    available: Math.max(round2(r.completed - r.withdrawn - r.pendingWithdrawal), 0),
  }));
  rows.sort((a, b) => b.available - a.available);
  return rows;
}

export async function loadLedger(maxBookings = 1000) {
  const settings = await getFinanceSettings();
  const [cSnap, wSnap] = await Promise.all([
    getDocs(
      query(
        collection(db, consultationsCollection),
        orderBy("consultationTime", "desc"),
        limit(maxBookings)
      )
    ),
    getDocs(collection(db, withdrawalsCollection)).catch(() => ({ docs: [] })),
  ]);

  const credits = cSnap.docs
    .map((d) => ({ id: d.id, ...d.data() }))
    .filter((c) => num(c.payment?.amountPaise) > 0 || (c.payment?.status === "free" && c.doctorFee !== undefined))
    .map((c) => creditFromConsultation(c, settings));
  const withdrawals = wSnap.docs.map((d) => entryFromWithdrawal({ id: d.id, ...d.data() }));

  const entries = [...credits, ...withdrawals].sort((a, b) => b.date - a.date);
  return { settings, entries, balances: buildBalances(entries), truncated: cSnap.docs.length >= maxBookings };
}
